import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import emailjs from "@emailjs/browser";

import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import "./Contact.css";
import { useApiQuery } from "../api/client";

const schema = Yup.object({
  fullName: Yup.string().min(3, "Too short").required("Full name is required"),
  phone: Yup.string()
    .min(7, "Invalid phone")
    .max(20, "Invalid phone")
    .required("Phone is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  interestedIn: Yup.string().required("Please select an option"),
  message: Yup.string().min(10, "Write a bit more").required("Message is required"),
});

const Contact = () => {
  const { ref, inView } = useInView({ threshold: 0.18, triggerOnce: true });
  const { data: settings } = useApiQuery("/settings");
  const phoneDisplay = settings?.phoneNumber || "0322-7401538";
  const whatsappDisplay = settings?.whatsappNumber || "0322-7401538";

const formik = useFormik({
  initialValues: {
    fullName: "",
    phone: "",
    email: "",
    interestedIn: "Site Visit",
    message: "",
  },
  validationSchema: schema,
  onSubmit: async (values, { resetForm }) => {
    try {
      await emailjs.send(
        "service_sarprtc",
        "template_ey99c6y",
        {
          fullName: values.fullName,
          phone: values.phone,
          email: values.email,
          interestedIn: values.interestedIn,
          message: values.message,
        },
        "i7FCoY5M4Np0k6efr"
      );

      toast.success("Thanks! We received your request. We'll contact you soon.");
      resetForm();
    } catch (err) {
      console.error("EmailJS Error:", err);
      toast.error("Something went wrong. Please try again.");
    }
  },
})

  const err = (name) => formik.touched[name] && formik.errors[name];

  return (
    <section className="con" id="contact" ref={ref}>
      <div className="con__container">
        <motion.div
          className="con__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="con__kicker">CONTACT</div>
          <h2 className="con__title">Let’s talk. We’ll share pricing, floor plans, and availability.</h2>
          <p className="con__sub">
            Fill the form and we’ll get back quickly. If you prefer, contact us directly using the details below.
          </p>
        </motion.div>

        <div className="con__grid">
          {/* Left info */}
          <motion.div
            className="con__info"
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.06 }}
          >
            <div className="con__infoCard">
              <div className="con__infoTitle">Quick Contact</div>

              <div className="con__infoRow">
                <div className="con__infoIcon">
                  <PhoneRoundedIcon />
                </div>
                <div className="con__infoText">
                  <div className="con__infoLabel">Phone</div>
                  <div className="con__infoValue">{phoneDisplay}</div>
                </div>
              </div>

              <div className="con__infoRow">
                <div className="con__infoIcon">
                  <WhatsAppIcon />
                </div>
                <div className="con__infoText">
                  <div className="con__infoLabel">Whatsapp</div>
                  <div className="con__infoValue">{whatsappDisplay}</div>
                </div>
              </div>

              <div className="con__infoRow">
                <div className="con__infoIcon">
                  <LocationOnRoundedIcon />
                </div>
                <div className="con__infoText">
                  <div className="con__infoLabel">Head Office</div>
                  <div className="con__infoValue">11-B Commercial Al Kabir Town Pahse-2 Main Raiwind Road Lahore </div>
                </div>
              </div>

              {/* <div className="con__note">
                Prefer WhatsApp? We’ll add a WhatsApp button here next.
              </div> */}
            </div>

            <div className="con__trust">
              <div className="con__trustItem">✓ Transparent documentation</div>
              <div className="con__trustItem">✓ Flexible payment plans</div>
              <div className="con__trustItem">✓ Limited premium units</div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            className="con__formWrap"
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12 }}
          >
            <form className="con__form" onSubmit={formik.handleSubmit}>
              <div className="con__row">
                <div className="con__field">
                  <label className="con__label">Full Name</label>
                  <input
                    className={`con__input ${err("fullName") ? "is-error" : ""}`}
                    name="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Your full name"
                  />
                  {err("fullName") ? <div className="con__error">{formik.errors.fullName}</div> : null}
                </div>

                <div className="con__field">
                  <label className="con__label">Phone</label>
                  <input
                    className={`con__input ${err("phone") ? "is-error" : ""}`}
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="+92 3xx xxxxxxx"
                  />
                  {err("phone") ? <div className="con__error">{formik.errors.phone}</div> : null}
                </div>
              </div>

              <div className="con__row">
                <div className="con__field">
                  <label className="con__label">Email</label>
                  <input
                    className={`con__input ${err("email") ? "is-error" : ""}`}
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="you@email.com"
                  />
                  {err("email") ? <div className="con__error">{formik.errors.email}</div> : null}
                </div>

                <div className="con__field">
                  <label className="con__label">Interested In</label>
                  <select
                    className={`con__input ${err("interestedIn") ? "is-error" : ""}`}
                    name="interestedIn"
                    value={formik.values.interestedIn}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option>Site Visit</option>
                    <option>Pricing</option>
                    <option>Floor Plan</option>
                    <option>Availability</option>
                    <option>Payment Plan</option>
                  </select>
                  {err("interestedIn") ? (
                    <div className="con__error">{formik.errors.interestedIn}</div>
                  ) : null}
                </div>
              </div>

              <div className="con__field">
                <label className="con__label">Message</label>
                <textarea
                  className={`con__textarea ${err("message") ? "is-error" : ""}`}
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Tell us what you need: floor plan, pricing, installment schedule, etc."
                  rows={5}
                />
                {err("message") ? <div className="con__error">{formik.errors.message}</div> : null}
              </div>

              <button className="con__btn" type="submit">
                Send Request <ArrowOutwardRoundedIcon />
              </button>

              <div className="con__small">
                By submitting, you agree to be contacted about this project.
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
